import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AsyncSubject, first, firstValueFrom, Observable } from 'rxjs';

export type ConfigType = {
  production: boolean;
  aggregationDataURL: string;
  aggregatedDetailsURL: string;
  newBuildsAggregationDataURL: string;
  newBuildsAggregatedDetailsURL: string;
  newBuildSearchOptionsURL: string;
  newBuildStccCarTypesURL: string;
  carTypeOptionsURL: string;
  clientProfileURL: string;
  clientCustomerURL: string;
  clientAccountURL: string;
  cognitoAppClientId: string;
  cognitoUserPoolId: string;
  checkUserDomainURL: string;
  railPerformanceForecastURL: string;
  railPerformanceURL: string;
  railPerformanceAPIURL: string;
  mixpanelToken: string;
  countriesURL: string;
  firstTimeUserURL: string;
  fleetFinderURL: string;
  imagesURL: string;
  leaseMechOptionsURL: string;
  lockoutUserURL: string;
  logInURL: string;
  presignedURL: string;
  searchOptionsURL: string;
  signUpURL: string;
  stccCarTypesURL: string;
  submitQuoteURL: string;
  updatePasswordURL: string;
  validateAccountURL: string;
  validateSignupTokenURL: string;
  sdsFileUpload: string;
  sdsGetFile: string;
  sdsDeleteFile: string;
  submitFeedback: string;
  signupUnprovisionedUsers: string;
  alertRailcarInventory: string;
  submitdraftQuote: string;
  draftQuoteDetails: string;
  deleteQuoteDetails: string;
  updateFavoriteQuote: string;
  auth0domain: string;
  auth0clientId: string;
  auth0audience: string;
};

@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  private readonly CONFIG_URL: string = 'assets/json/config.json';
  private static _config_: ConfigType = <ConfigType>{};
  _config_$ = new AsyncSubject<ConfigType>();
  private _httpClient_: HttpClient;

  public get aggregationDataURL(): string {
    return ConfigService._config_.aggregationDataURL;
  }
  public get aggregatedDetailsURL(): string {
    return ConfigService._config_.aggregatedDetailsURL;
  }
  public get carTypeOptionsURL(): string {
    return ConfigService._config_.carTypeOptionsURL;
  }
  public get clientProfileURL(): string {
    return ConfigService._config_.clientProfileURL;
  }

  public get checkUserDomainURL(): string {
    return ConfigService._config_.checkUserDomainURL;
  }

  public get railPerformanceForecastURL(): string {
    return ConfigService._config_.railPerformanceForecastURL;
  }

  public get railPerformanceURL(): string {
    return ConfigService._config_.railPerformanceURL;
  }

  public get railPerformanceAPIURL(): string {
    return ConfigService._config_.railPerformanceAPIURL;
  }

  public get auth0domain(): string {
    return ConfigService._config_.auth0domain;
  }

  public get auth0clientId(): string {
    return ConfigService._config_.auth0clientId;
  }

  public get auth0audience(): string {
    return ConfigService._config_.auth0audience;
  }

  public get clientCustomerURL(): string {
    return ConfigService._config_.clientCustomerURL;
  }

  public get clientAccountURL(): string {
    return ConfigService._config_.clientAccountURL;
  }

  public get cognitoAppClientId(): string {
    return ConfigService._config_.cognitoAppClientId;
  }
  public get cognitoUserPoolId(): string {
    return ConfigService._config_.cognitoUserPoolId;
  }
  public get countriesURL(): string {
    return ConfigService._config_.countriesURL;
  }
  public get firstTimeUserURL(): string {
    return ConfigService._config_.firstTimeUserURL;
  }
  public get fleetFinderURL(): string {
    return ConfigService._config_.fleetFinderURL;
  }
  public get imagesURL(): string {
    return ConfigService._config_.imagesURL;
  }
  public get leaseMechOptionsURL(): string {
    return ConfigService._config_.leaseMechOptionsURL;
  }
  public get lockoutUserURL(): string {
    return ConfigService._config_.lockoutUserURL;
  }
  public get logInURL(): string {
    return ConfigService._config_.logInURL;
  }
  public get presignedURL(): string {
    return ConfigService._config_.presignedURL;
  }
  public get searchOptionsURL(): string {
    return ConfigService._config_.searchOptionsURL;
  }
  public get signUpURL(): string {
    return ConfigService._config_.signUpURL;
  }
  public get stccCarTypesURL(): string {
    return ConfigService._config_.stccCarTypesURL;
  }
  public get submitQuoteURL(): string {
    return ConfigService._config_.submitQuoteURL;
  }
  public get updatePasswordURL(): string {
    return ConfigService._config_.updatePasswordURL;
  }
  public get validateAccountURL(): string {
    return ConfigService._config_.validateAccountURL;
  }
  public get validateSignupTokenURL(): string {
    return ConfigService._config_.validateSignupTokenURL;
  }
  public get sdsFileUpload(): string {
    return ConfigService._config_.sdsFileUpload;
  }
  public get sdsGetFile(): string {
    return ConfigService._config_.sdsGetFile;
  }
  public get sdsDeleteFile(): string {
    return ConfigService._config_.sdsDeleteFile;
  }
  public get submitFeedback(): string {
    return ConfigService._config_.submitFeedback;
  }

  public get signupUnprovisionedUsers(): string {
    return ConfigService._config_.signupUnprovisionedUsers;
  }
  public get alertRailcarInventory(): string {
    return ConfigService._config_.alertRailcarInventory;
  }
  public get updateFavoriteQuote(): string {
    return ConfigService._config_.updateFavoriteQuote;
  }
  public get submitdraftQuote(): string {
    return ConfigService._config_.submitdraftQuote;
  }
  public get draftQuoteDetails(): string {
    return ConfigService._config_.draftQuoteDetails;
  }
  public get deleteQuoteDetails(): string {
    return ConfigService._config_.deleteQuoteDetails;
  }

  public get newBuildsAggregationDataURL(): string {
    return ConfigService._config_.newBuildsAggregationDataURL;
  }

  public get newBuildsAggregatedDetailsURL(): string {
    return ConfigService._config_.newBuildsAggregatedDetailsURL;
  }

  public get newBuildSearchOptionsURL(): string {
    return ConfigService._config_.newBuildSearchOptionsURL;
  }

  public get newBuildStccCarTypesURL(): string {
    return ConfigService._config_.newBuildStccCarTypesURL;
  }

  constructor(httpClient: HttpClient) {
    this._httpClient_ = httpClient;
    this.loadConfig();
    const subscriptionHandler = (data: ConfigType) => {
      ConfigService._config_ = JSON.parse(JSON.stringify(data));
      this._config_$.next(ConfigService._config_);
      this._config_$.complete();
    };

    const config$: Observable<ConfigType> = this._httpClient_.get<ConfigType>(
      this.CONFIG_URL
    );
    config$.pipe(first()).subscribe(subscriptionHandler);
  }

  private loadConfig() {
    this._httpClient_
      .get<ConfigType>(this.CONFIG_URL)
      .pipe(first())
      .subscribe({
        next: (data) => {
          ConfigService._config_ = data;
          this._config_$.next(data);
          this._config_$.complete();
        },
        error: (error) => {
          console.error('Failed to load config:', error);
          this._config_$.error(error);
        },
      });
  }

  async loadAsyncConfig(): Promise<void> {
    try {
      console.log('Fetching configuration from:', this.CONFIG_URL);
      const data = await firstValueFrom(
        this._httpClient_.get<ConfigType>(this.CONFIG_URL)
      );
      ConfigService._config_ = data;
      console.log('Configuration fetched:', data);
    } catch (error) {
      console.error('Failed to load configuration:', error);
      throw error;
    }
  }

  // Example getter
  public get mixpanelToken(): string {
    return ConfigService._config_ ? ConfigService._config_.mixpanelToken : '';
  }
}
